import React, { useState } from 'react';

interface CompanySettings {
  companyName: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  taxId: string;
  businessHours: string;
  timezone: string;
}

interface UserSettings {
  name: string;
  email: string;
  phone: string;
  role: string;
  notifications: {
    email: boolean;
    sms: boolean;
    push: boolean;
  };
  theme: 'light' | 'dark';
  language: string;
}

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'company' | 'user' | 'billing' | 'integrations'>('company');
  
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    companyName: 'HVAC Pro Services',
    address: '123 Business Ave, City, ST 12345',
    phone: '(555) 123-4567',
    email: 'info@hvacpro.com',
    website: 'www.hvacpro.com',
    taxId: '12-3456789',
    businessHours: '8:00 AM - 6:00 PM',
    timezone: 'America/New_York'
  });

  const [userSettings, setUserSettings] = useState<UserSettings>({
    name: 'Admin User',
    email: 'admin@hvaccrm.com',
    phone: '(555) 987-6543',
    role: 'Administrator',
    notifications: {
      email: true,
      sms: false,
      push: true
    },
    theme: 'light',
    language: 'English'
  });

  const [billingInfo] = useState({
    plan: 'Standard',
    nextBilling: '2024-02-15',
    amount: '$49.00',
    paymentMethod: '**** **** **** 1234'
  });

  const handleCompanySettingsChange = (field: keyof CompanySettings, value: string) => {
    setCompanySettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUserSettingsChange = (field: keyof UserSettings, value: any) => {
    setUserSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNotificationChange = (type: keyof UserSettings['notifications']) => {
    setUserSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [type]: !prev.notifications[type]
      }
    }));
  };

  const renderCompanySettings = () => (
    <div className="card">
      <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Company Information</h3>
      <form style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
        <div className="form-group">
          <label>Company Name *</label>
          <input 
            type="text" 
            value={companySettings.companyName}
            onChange={(e) => handleCompanySettingsChange('companyName', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Phone Number *</label>
          <input 
            type="tel" 
            value={companySettings.phone}
            onChange={(e) => handleCompanySettingsChange('phone', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Email Address *</label>
          <input 
            type="email" 
            value={companySettings.email}
            onChange={(e) => handleCompanySettingsChange('email', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Website</label>
          <input 
            type="text" 
            value={companySettings.website}
            onChange={(e) => handleCompanySettingsChange('website', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Tax ID</label>
          <input 
            type="text" 
            value={companySettings.taxId}
            onChange={(e) => handleCompanySettingsChange('taxId', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Timezone</label>
          <select 
            value={companySettings.timezone}
            onChange={(e) => handleCompanySettingsChange('timezone', e.target.value)}
          >
            <option value="America/New_York">Eastern Time</option>
            <option value="America/Chicago">Central Time</option>
            <option value="America/Denver">Mountain Time</option>
            <option value="America/Los_Angeles">Pacific Time</option>
          </select>
        </div>
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label>Business Address *</label>
          <input 
            type="text" 
            value={companySettings.address}
            onChange={(e) => handleCompanySettingsChange('address', e.target.value)}
          />
        </div>
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label>Business Hours</label>
          <input 
            type="text" 
            value={companySettings.businessHours}
            onChange={(e) => handleCompanySettingsChange('businessHours', e.target.value)}
            placeholder="e.g., 8:00 AM - 6:00 PM, Monday - Friday"
          />
        </div>
      </form>
      <div style={{ marginTop: '20px' }}>
        <button className="btn btn-primary">Save Company Settings</button>
      </div>
    </div>
  );

  const renderUserSettings = () => (
    <div>
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>User Profile</h3>
        <form style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div className="form-group">
            <label>Full Name *</label>
            <input 
              type="text" 
              value={userSettings.name}
              onChange={(e) => handleUserSettingsChange('name', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Email Address *</label>
            <input 
              type="email" 
              value={userSettings.email}
              onChange={(e) => handleUserSettingsChange('email', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <input 
              type="tel" 
              value={userSettings.phone}
              onChange={(e) => handleUserSettingsChange('phone', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Role</label>
            <input 
              type="text" 
              value={userSettings.role}
              readOnly
              style={{ backgroundColor: '#f8f9fa' }}
            />
          </div>
        </form>
        <div style={{ marginTop: '20px' }}>
          <button className="btn btn-primary">Update Profile</button>
          <button className="btn btn-secondary" style={{ marginLeft: '10px' }}>Change Password</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Notifications</h3>
        <div style={{ display: 'grid', gap: '15px' }}>
          {Object.entries(userSettings.notifications).map(([key, value]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <strong style={{ textTransform: 'capitalize' }}>{key} Notifications</strong>
                <br />
                <small style={{ color: '#6c757d' }}>
                  Receive notifications via {key}
                </small>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={value}
                  onChange={() => handleNotificationChange(key as keyof UserSettings['notifications'])}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ 
                  width: '40px', 
                  height: '20px', 
                  backgroundColor: value ? '#007bff' : '#ccc',
                  borderRadius: '10px',
                  position: 'relative',
                  transition: 'background-color 0.3s'
                }}>
                  <span style={{
                    position: 'absolute',
                    top: '2px',
                    left: value ? '22px' : '2px',
                    width: '16px',
                    height: '16px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    transition: 'left 0.3s'
                  }}></span>
                </span>
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Preferences</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div className="form-group">
            <label>Theme</label>
            <select 
              value={userSettings.theme}
              onChange={(e) => handleUserSettingsChange('theme', e.target.value)}
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </div>
          <div className="form-group">
            <label>Language</label>
            <select 
              value={userSettings.language}
              onChange={(e) => handleUserSettingsChange('language', e.target.value)}
            >
              <option value="English">English</option>
              <option value="Spanish">Spanish</option>
              <option value="French">French</option>
            </select>
          </div>
        </div>
        <div style={{ marginTop: '20px' }}>
          <button className="btn btn-primary">Save Preferences</button>
        </div>
      </div>
    </div>
  );

  const renderBillingSettings = () => (
    <div>
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Current Plan</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <p><strong>Plan:</strong> {billingInfo.plan}</p>
            <p><strong>Next Billing:</strong> {billingInfo.nextBilling}</p>
          </div>
          <div>
            <p><strong>Amount:</strong> {billingInfo.amount}/month</p>
            <p><strong>Payment Method:</strong> {billingInfo.paymentMethod}</p>
          </div>
        </div>
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
          <button className="btn btn-primary">Upgrade Plan</button>
          <button className="btn btn-secondary">Update Payment Method</button>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Billing History</h3>
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Invoice</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>2024-01-15</td>
              <td>Standard Plan - Monthly</td>
              <td>$49.00</td>
              <td><span className="status status-completed">Paid</span></td>
              <td><button style={{ fontSize: '12px', padding: '4px 8px' }}>Download</button></td>
            </tr>
            <tr>
              <td>2023-12-15</td>
              <td>Standard Plan - Monthly</td>
              <td>$49.00</td>
              <td><span className="status status-completed">Paid</span></td>
              <td><button style={{ fontSize: '12px', padding: '4px 8px' }}>Download</button></td>
            </tr>
            <tr>
              <td>2023-11-15</td>
              <td>Standard Plan - Monthly</td>
              <td>$49.00</td>
              <td><span className="status status-completed">Paid</span></td>
              <td><button style={{ fontSize: '12px', padding: '4px 8px' }}>Download</button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderIntegrations = () => (
    <div>
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Available Integrations</h3>
        <div style={{ display: 'grid', gap: '20px' }}>
          {[
            { name: 'QuickBooks', description: 'Sync financial data and invoicing', connected: true },
            { name: 'Google Calendar', description: 'Sync appointments and schedules', connected: true },
            { name: 'Mailchimp', description: 'Email marketing automation', connected: false },
            { name: 'Stripe', description: 'Process payments online', connected: false },
            { name: 'Slack', description: 'Team communication and notifications', connected: false }
          ].map((integration, index) => (
            <div key={index} style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '15px',
              border: '1px solid #e9ecef',
              borderRadius: '8px'
            }}>
              <div>
                <strong>{integration.name}</strong>
                <br />
                <small style={{ color: '#6c757d' }}>{integration.description}</small>
              </div>
              <button 
                className={`btn ${integration.connected ? 'btn-secondary' : 'btn-primary'}`}
                style={{ minWidth: '100px' }}
              >
                {integration.connected ? 'Disconnect' : 'Connect'}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>API Settings</h3>
        <div className="form-group">
          <label>API Key</label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input 
              type="text" 
              value="hvac_live_sk_1234567890abcdef"
              readOnly
              style={{ backgroundColor: '#f8f9fa', flex: 1 }}
            />
            <button className="btn btn-secondary">Regenerate</button>
          </div>
          <small style={{ color: '#6c757d' }}>
            Use this API key to integrate with third-party applications
          </small>
        </div>
        <div style={{ marginTop: '20px' }}>
          <button className="btn btn-primary">View API Documentation</button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ color: '#2c3e50', marginBottom: '10px' }}>Settings</h1>
        <p style={{ color: '#6c757d' }}>Manage your account and system preferences</p>
      </div>

      <div style={{ display: 'flex', gap: '5px', marginBottom: '30px', borderBottom: '1px solid #e9ecef' }}>
        {[
          { id: 'company', label: 'Company' },
          { id: 'user', label: 'User Profile' },
          { id: 'billing', label: 'Billing' },
          { id: 'integrations', label: 'Integrations' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            style={{
              padding: '12px 20px',
              border: 'none',
              backgroundColor: 'transparent',
              color: activeTab === tab.id ? '#007bff' : '#6c757d',
              borderBottom: activeTab === tab.id ? '2px solid #007bff' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: activeTab === tab.id ? 'bold' : 'normal'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div>
        {activeTab === 'company' && renderCompanySettings()}
        {activeTab === 'user' && renderUserSettings()}
        {activeTab === 'billing' && renderBillingSettings()}
        {activeTab === 'integrations' && renderIntegrations()}
      </div>
    </div>
  );
};

export default SettingsPage;